import {batch} from 'react-redux';
import {selectGlobalPresentId} from 'stores/selectors/undoRedoSelectors';
import {restoreVirtualTrayState} from 'stores/actions/virtualTrayAction';
import {selectProtocolCheckpointHistoryId} from 'stores/selectors/protocolSelectors';

import {modifyObjectValue} from '../../lib/objectHelper';
import * as undoRedoAction from '../actions/undoRedoAction';
import * as cardStateAction from '../actions/cardStateAction';
import * as labwareStateAction from '../actions/labwareStateAction';
import * as protocolEditorAction from '../actions/protocolEditorAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import * as normalizationStateAction from '../actions/normalizationStateAction';
import {saveState} from './stateBusiness';

const FIRST_HISTORY_ID = 1;

export const getGlobalUndoRedoState = () => (dispatch, getState) => {
  const {undoRedoState} = getState();
  const selectedUndoRedoState = undoRedoState.global;

  return selectedUndoRedoState ?? {future: [], present: {}, past: []};
};

const checkIsSetToPendingSave = (currentCardState, cardStateToCompare) => {
  const currCardStateString = JSON.stringify(currentCardState);
  const cardStateToCompareString = JSON.stringify(cardStateToCompare);

  if (currCardStateString === cardStateToCompareString) return false;
  return true;
};

export const globalAddUndoItem =
  (oldCard, otherState = {}, otherOldState = {}, setPendingSave = null) =>
  (dispatch, getState) => {
    const {
      cardState,
      protocolEditorState,
      labwareState,
      temporaryState,
      normalizationState,
      undoRedoState,
      virtualTrayState,
    } = getState();

    const unselectedLabwareState = {};
    let shouldSetPendingSave = setPendingSave;

    if (shouldSetPendingSave === null) {
      const {global: globalOld} = undoRedoState || {};
      shouldSetPendingSave = checkIsSetToPendingSave(cardState, globalOld?.present?.cardState);
    }

    // set labwareState isSelected property to false
    // to prevent labware floating button to showing up
    Object.keys(labwareState).forEach((e) => {
      unselectedLabwareState[e] = {
        ...labwareState[e],
        isSelected: false,
      };
    });

    let oldObj = null;
    const presentId = selectGlobalPresentId(getState()) || FIRST_HISTORY_ID;

    if (oldCard) {
      oldObj = {
        labwareState: unselectedLabwareState,
        protocolEditorState,
        temporaryState,
        normalizationState,
        cardState: oldCard,
        otherState,
        virtualTrayState,
        ...otherOldState,
        id: presentId,
      };
    }

    const presentState = {
      labwareState: unselectedLabwareState,
      cardState,
      virtualTrayState,
      protocolEditorState: {
        ...protocolEditorState,
        isPendingSaveExist: shouldSetPendingSave || protocolEditorState.isPendingSaveExist,
        autosave: protocolEditorState.autosave,
      },
      temporaryState,
      normalizationState,
      otherState,
      id: presentId + 1,
    };

    dispatch(undoRedoAction.newItem('global', presentState, oldObj));

    dispatch(saveState(shouldSetPendingSave));
  };

export const globalUndoRedoUpdatePresentState =
  (stateKey, newstate = {}) =>
  (dispatch, getState) => {
    const {undoRedoState} = getState();
    const {present} = undoRedoState.global;

    const oldState = present[stateKey];

    const newPresent = {...present, [stateKey]: {...oldState, ...newstate}};

    dispatch(undoRedoAction.updatePresentItem('global', newPresent));
  };

export function globalUndoRedoClearItem() {
  return (dispatch) => {
    dispatch(undoRedoAction.clearItem('global'));
  };
}

export const globalUndo = () => (dispatch, getState) => {
  const globalUndoRedoState = dispatch(getGlobalUndoRedoState());
  const {protocolEditorState, virtualTrayState} = getState();

  if (globalUndoRedoState?.past?.length === 0) {
    return null;
  }

  let response = dispatch(undoRedoAction.undoItem('global'));

  if (!response) {
    return null;
  }

  const {cardState, labwareState, id: historyIdToLoad} = response;

  const checkpointHistoryId = selectProtocolCheckpointHistoryId(getState()) || FIRST_HISTORY_ID;
  const isOnCheckpoint = historyIdToLoad === checkpointHistoryId;

  response = modifyObjectValue(response, 'protocolEditorState', {
    copiedPipettingParameter: protocolEditorState.copiedPipettingParameter,
    isPendingSaveExist: !isOnCheckpoint,
    autosave: protocolEditorState.autosave,
  });

  batch(() => {
    dispatch(labwareStateAction.restoreLabwareState(labwareState));
    dispatch(cardStateAction.restoreCardTaskState(cardState.tasks));
    dispatch(cardStateAction.restoreCardState(cardState.cards));
    dispatch(cardStateAction.restoreGroupState(cardState.groups));
    dispatch(protocolEditorAction.restoreProtocolEditorState(response.protocolEditorState));
    dispatch(temporaryStateAction.restoreTemporaryState(response.temporaryState));
    dispatch(normalizationStateAction.restoreNormalizationState(response.normalizationState));
    dispatch(restoreVirtualTrayState({...virtualTrayState, ...response.virtualTrayState}));
  });

  return response;
};

export const globalRedo = () => (dispatch, getState) => {
  const globalUndoRedoState = dispatch(getGlobalUndoRedoState());
  const {protocolEditorState, virtualTrayState} = getState();

  if (globalUndoRedoState?.future?.length === 0) {
    return null;
  }

  let response = dispatch(undoRedoAction.redoItem('global'));

  if (!response) {
    return null;
  }

  const {cardState, labwareState, id: historyIdToLoad} = response;

  const checkpointHistoryId = selectProtocolCheckpointHistoryId(getState());
  const isOnCheckpoint = historyIdToLoad === checkpointHistoryId;

  response = modifyObjectValue(response, 'protocolEditorState', {
    copiedPipettingParameter: protocolEditorState.copiedPipettingParameter,
    isPendingSaveExist: !isOnCheckpoint,
    autosave: protocolEditorState.autosave,
  });

  batch(() => {
    dispatch(labwareStateAction.restoreLabwareState(labwareState));
    dispatch(cardStateAction.restoreCardTaskState(cardState.tasks));
    dispatch(cardStateAction.restoreCardState(cardState.cards));
    dispatch(cardStateAction.restoreGroupState(cardState.groups));
    dispatch(protocolEditorAction.restoreProtocolEditorState(response.protocolEditorState));
    dispatch(temporaryStateAction.restoreTemporaryState(response.temporaryState));
    dispatch(normalizationStateAction.restoreNormalizationState(response.normalizationState));
    dispatch(restoreVirtualTrayState({...virtualTrayState, ...response.virtualTrayState}));
  });

  return response;
};
